import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, type StaffAccount } from '@chicare/shared';

import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Icon } from '@/components/icons/Icon';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { AdminError } from './AdminError';

/**
 * The people who work here, and what each of them signs in to.
 *
 * ── WHY THIS IS NOT THE PEOPLE REGISTER ─────────────────────────────────────
 *
 * The People screen is read-only on purpose: a buyer proves who they are with a
 * code sent to a channel, so anybody who could repoint that channel could take
 * the account. A staff account has no codes to steal — it has a password
 * somebody at ChiCare set, at a door buyers never see. So it is made here.
 *
 * ── WHAT THIS SCREEN WILL NOT DO ────────────────────────────────────────────
 *
 * It will not make a super admin, edit one, or edit the person using it. Those
 * are not oversights and the buttons are absent rather than disabled: a super
 * admin sits outside the permission system entirely, so being able to mint one
 * from here would make every limit on the roles screen decorative.
 *
 * ── AND WHY "REMOVE" USUALLY MEANS REVOKE ───────────────────────────────────
 *
 * A staff account is the name on audit-log rows going back months (§9.11).
 * Deleting it would leave decisions about somebody's money with nobody's name
 * against them, so access is taken away and the history survives. An account
 * that never did anything is deleted outright — there is nothing to preserve.
 */
export function StaffScreen() {
  const [editing, setEditing] = useState<StaffAccount | 'new' | null>(null);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['admin', 'staff-accounts'],
    queryFn: ({ signal }) => api.staffAccounts(signal),
  });

  if (isError) {
    return <AdminError title="We couldn't load the staff" error={error} onRetry={() => refetch()} />;
  }

  if (editing) {
    return (
      <StaffForm
        account={editing === 'new' ? null : editing}
        roles={data?.roles ?? []}
        onDone={() => setEditing(null)}
      />
    );
  }

  return (
    <>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <p className="m-0 max-w-[62ch] text-[13px] leading-relaxed text-muted">
          Everybody with a console account. Give somebody a password and a job, and they see only
          what that job covers — widen it, narrow it or take it back any day.
        </p>

        <Button onClick={() => setEditing('new')}>Add someone</Button>
      </div>

      {isLoading ? (
        <div className="space-y-2.5">
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} className="h-[76px] w-full" radius="card" />
          ))}
        </div>
      ) : (
        <div className="overflow-hidden rounded-card border border-line bg-white">
          {(data?.staff ?? []).map((person) => (
            <StaffRow key={person.id} person={person} onEdit={() => setEditing(person)} />
          ))}
        </div>
      )}
    </>
  );
}

function StaffRow({ person, onEdit }: { person: StaffAccount; onEdit: () => void }) {
  const revoked = person.status !== 'active';

  const body = (
    <>
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-2">
          <span className={cn('text-[14px] font-bold', revoked ? 'text-muted' : 'text-ink')}>
            {person.name}
          </span>

          <Badge tone={person.is_super_admin ? 'verified' : 'neutral'}>
            {person.account_type_label}
          </Badge>

          {revoked ? <Badge tone="danger">Access revoked</Badge> : null}
        </span>

        <span className="mt-0.5 block break-all text-[12.5px] text-muted">{person.email}</span>

        <span className="mt-1 block font-mono text-[11px] text-faint">
          {person.staff_role
            ? `${person.staff_role.name} · ${person.permission_count} permission${person.permission_count === 1 ? '' : 's'}`
            : person.is_super_admin
              ? 'Everything, by definition'
              : 'No role yet'}
        </span>
      </span>

      {person.is_super_admin ? (
        /* Said, rather than left as a missing button somebody assumes is a bug. */
        <span className="mt-1 shrink-0 text-[11.5px] text-faint">Not editable here</span>
      ) : (
        <Icon name="chevron" size={16} strokeWidth={2} className="mt-1 shrink-0 text-muted" />
      )}
    </>
  );

  if (person.is_super_admin) {
    return (
      <div className="flex w-full items-start gap-3 border-b border-line px-4 py-3 last:border-b-0">
        {body}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onEdit}
      data-testid={`staff-${person.id}`}
      className="flex w-full min-h-11 items-start gap-3 border-b border-line bg-white px-4 py-3 text-left last:border-b-0 hover:bg-paper"
    >
      {body}
    </button>
  );
}

function StaffForm({
  account,
  roles,
  onDone,
}: {
  account: StaffAccount | null;
  roles: Array<{ id: number; name: string }>;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();

  const [name, setName] = useState(account?.name ?? '');
  const [email, setEmail] = useState(account?.email ?? '');
  const [phone, setPhone] = useState(account?.phone ?? '');
  const [password, setPassword] = useState('');
  const [accountType, setAccountType] = useState(account?.account_type ?? 'admin');
  const [roleId, setRoleId] = useState<number | null>(account?.staff_role?.id ?? null);
  const [status, setStatus] = useState(account?.status ?? 'active');
  const [confirmingRemove, setConfirmingRemove] = useState(false);

  const done = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'staff-accounts'] });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'staff-roles'] });
    onDone();
  };

  const save = useMutation({
    mutationFn: () =>
      account
        ? api.updateStaffAccount(account.id, {
            name,
            email,
            phone,
            account_type: accountType,
            staff_role_id: roleId,
            status,
            // Empty means leave it. Sending a blank would be sending a change.
            ...(password === '' ? {} : { password }),
          })
        : api.createStaffAccount({
            name,
            email,
            phone,
            password,
            account_type: accountType,
            staff_role_id: roleId,
          }),
    onSuccess: done,
  });

  const remove = useMutation({
    mutationFn: () => api.removeStaffAccount(account!.id),
    onSuccess: done,
  });

  const error = [save.error, remove.error].find((e): e is ApiError => e instanceof ApiError);
  const field = (key: string) => error?.fieldErrors[key]?.[0] ?? null;

  const incomplete =
    name.trim() === '' || email.trim() === '' || (!account && password.length < 10);

  return (
    <div className="mx-auto w-full max-w-[600px]">
      <button
        type="button"
        onClick={onDone}
        className="mb-4 inline-flex min-h-11 items-center gap-1.5 bg-transparent p-0 text-[13px] font-semibold text-muted"
      >
        <Icon name="back" size={15} strokeWidth={2} />
        All staff
      </button>

      <h2 className="m-0 font-display text-[22px] font-bold tracking-[-0.02em]">
        {account ? account.name : 'A new colleague'}
      </h2>

      <div className="mt-4 space-y-3 rounded-card border border-line bg-white p-4 sm:p-5">
        <Input
          label="Their name"
          value={name}
          placeholder="Ada Nwosu"
          error={field('name')}
          onChange={(event) => setName(event.target.value)}
        />

        <Input
          label="Email they sign in with"
          type="email"
          value={email}
          placeholder="ada@chicare.ng"
          error={field('email')}
          onChange={(event) => setEmail(event.target.value)}
        />

        <Input
          label="Phone (optional)"
          mono
          value={phone}
          placeholder="0803 123 4567"
          error={field('phone')}
          onChange={(event) => setPhone(event.target.value)}
        />

        <Input
          label={account ? 'New password' : 'Password'}
          type="password"
          value={password}
          autoComplete="new-password"
          error={field('password')}
          hint={
            account
              ? 'Leave this empty to keep their current one. Changing it signs them out everywhere.'
              : 'At least 10 characters. Tell it to them yourself — ChiCare does not email passwords.'
          }
          onChange={(event) => setPassword(event.target.value)}
        />
      </div>

      <p className="m-0 mt-5 font-mono text-[10px] uppercase tracking-[0.12em] text-faint">
        What kind of account
      </p>

      <div className="mt-2 flex flex-wrap gap-2">
        {[
          ['admin', 'Staff', 'Works in the console. What they may do comes from their role.'],
          ['pharmacist', 'Pharmacist', 'A registered pharmacist. Usually the clinical desk.'],
        ].map(([value, label, blurb]) => (
          <button
            key={value}
            type="button"
            onClick={() => setAccountType(value!)}
            className={cn(
              'min-h-11 flex-1 basis-[220px] rounded-card border px-3.5 py-2.5 text-left',
              accountType === value ? 'border-blue bg-blue-tint/50' : 'border-line bg-white',
            )}
          >
            <span className="block text-[13.5px] font-bold text-ink">{label}</span>
            <span className="mt-0.5 block text-[11.5px] leading-snug text-muted">{blurb}</span>
          </button>
        ))}
      </div>

      <p className="m-0 mt-5 font-mono text-[10px] uppercase tracking-[0.12em] text-faint">
        Their job
      </p>

      <div className="mt-2 rounded-card border border-line bg-white p-4">
        <label className="block">
          <span className="mb-1.5 block text-[12.5px] font-semibold text-ink">Role</span>
          <select
            value={roleId ?? ''}
            data-testid="staff-role-select"
            onChange={(event) => setRoleId(event.target.value === '' ? null : Number(event.target.value))}
            className="min-h-11 w-full rounded-btn border border-line bg-white px-3 text-[14px] text-ink outline-none focus:border-blue"
          >
            <option value="">No role — nothing in the console</option>
            {roles.map((role) => (
              <option key={role.id} value={role.id}>
                {role.name}
              </option>
            ))}
          </select>
        </label>

        <p className="m-0 mt-2 text-[11.5px] leading-snug text-muted">
          The menu they see is drawn from this. A screen their role does not cover is not offered —
          and typing the address does not get them in either.
        </p>
      </div>

      {/* Only once they exist. Suspending an account being created is a
          contradiction, and offering it invites the accident. */}
      {account ? (
        <label className="mt-3 flex min-h-11 cursor-pointer items-start gap-2.5 rounded-card border border-line bg-white px-4 py-3">
          <input
            type="checkbox"
            checked={status === 'active'}
            onChange={(event) => setStatus(event.target.checked ? 'active' : 'suspended')}
            className="mt-0.5 h-4 w-4 shrink-0"
          />
          <span className="min-w-0 flex-1">
            <span className="block text-[13px] font-medium text-ink">Can sign in</span>
            <span className="mt-0.5 block text-[11.5px] leading-snug text-muted">
              Turn this off to pause somebody without losing the record of what they did.
            </span>
          </span>
        </label>
      ) : null}

      {error && !error.fieldErrors.name && !error.fieldErrors.email && !error.fieldErrors.password ? (
        <p
          role="alert"
          className="m-0 mt-4 rounded-card border border-danger bg-danger-tint px-3.5 py-2.5 text-[12.5px] font-medium text-danger"
        >
          {error.message}
        </p>
      ) : null}

      <div className="mt-5 flex flex-wrap items-center gap-2">
        <Button disabled={save.isPending || incomplete} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : account ? 'Save changes' : 'Create the account'}
        </Button>

        <Button variant="grey" onClick={onDone}>
          Cancel
        </Button>

        {account ? (
          <span className="ml-auto">
            {confirmingRemove ? (
              <span className="flex flex-wrap items-center gap-2">
                <span className="text-[12.5px] text-muted">Take their access away?</span>
                <Button
                  size="sm"
                  variant="grey"
                  disabled={remove.isPending}
                  onClick={() => remove.mutate()}
                >
                  {remove.isPending ? 'Removing…' : 'Remove access'}
                </Button>
                <button
                  type="button"
                  onClick={() => setConfirmingRemove(false)}
                  className="min-h-11 bg-transparent px-1 text-[12.5px] font-semibold text-muted"
                >
                  Keep it
                </button>
              </span>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmingRemove(true)}
                className="min-h-11 bg-transparent px-1 text-[12.5px] font-semibold text-muted"
              >
                Remove access
              </button>
            )}
          </span>
        ) : null}
      </div>

      {account ? (
        <p className="m-0 mt-3 text-[11.5px] leading-relaxed text-faint">
          Removing access signs them out everywhere and stops the password working. The account
          itself stays, because their name is on decisions in the audit log — unless they never made
          one, in which case it goes.
        </p>
      ) : null}
    </div>
  );
}
